import { createContext, type ReactNode, useContext } from 'react';
import type {
  DataTableColumn,
  DataTableRowEventParams,
  SearchValues,
  TableData
} from '@/components/custom/erp/data-table-v2';
import type { KhachHang, KhachHangInput } from '@/types/schemas';
import { exportMainColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

interface KhachHangContextValue {
  // CRUD operations
  addItem: (item: KhachHangInput) => Promise<KhachHang>;
  updateItem: (id: string, item: KhachHangInput) => Promise<KhachHang>;
  deleteItem: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  data: KhachHang[];
  currentPage: number;
  totalItems: number;
  handlePageChange: (page: number) => void;

  handleSearchChange: (values: SearchValues) => void;
  hasFilterParams: boolean;
  tables: TableData[];

  // Form state
  selectedObj: KhachHang | null;
  selectedRowIndex: string | null;
  showBulkDelete: boolean | null;
  handleCloseBulkDelete: () => void;
  handleRowClick: (params: DataTableRowEventParams<KhachHang>) => void;
  clearSelection: () => void;
  showForm: boolean;
  showDelete: boolean;
  formMode: 'add' | 'edit' | 'view';
  isCopyMode: boolean;
  isImportDialogOpen: boolean;
  setIsImportDialogOpen: (open: boolean) => void;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  handleAddClick: () => void;
  handleEditClick: () => void;
  handleViewClick: (row?: KhachHang) => void;
  handleDeleteClick: () => void;
  handleCopyClick: () => void;
  handleOpenImportExcel: () => void;
}

const KhachHangContext = createContext<KhachHangContextValue | null>(null);

export function useKhachHangContext() {
  const context = useContext(KhachHangContext);
  if (!context) {
    throw new Error('useKhachHangContext must be used within KhachHangProvider');
  }
  return context;
}

export function KhachHangProvider({ children }: { children: ReactNode }) {
  const {
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    data,
    detail,
    fetchDetail,
    currentPage,
    totalItems,
    handlePageChange,
    searchData
  } = useCRUD<KhachHang, KhachHangInput>({
    endpoint: QUERY_KEYS.KHACH_HANG,
    pageSize: 100
  });

  const {
    showForm,
    showDelete,
    selectedObj,
    selectedRowIndex,
    formMode,
    isCopyMode,
    showBulkDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<KhachHang>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : data || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = exportMainColumns(handleViewClick) as DataTableColumn<KhachHang>[];

  const tables: TableData[] = [
    {
      name: 'Tất cả',
      rows: displayData,
      columns
    },
    {
      name: 'Không hoạt động',
      rows: displayData.filter((row: KhachHang) => String(row.status) === '0'),
      columns
    }
  ];

  return (
    <KhachHangContext.Provider
      value={{
        // CRUD
        addItem,
        updateItem,
        deleteItem,
        refreshData,
        isLoading,
        data,
        currentPage: resolvedCurrentPage,
        totalItems: resolvedTotalItems,
        handlePageChange: onPageChange,
        showBulkDelete,
        handleCloseBulkDelete,

        handleSearchChange,
        hasFilterParams,
        tables,

        selectedObj: detail,
        selectedRowIndex,
        handleRowClick,
        clearSelection,
        showForm,
        showDelete,
        formMode,
        isCopyMode,
        isImportDialogOpen,
        setIsImportDialogOpen,
        handleCloseForm,
        handleCloseDelete,
        handleAddClick,
        handleEditClick,
        handleViewClick,
        handleDeleteClick,
        handleCopyClick,
        handleOpenImportExcel
      }}
    >
      {children}
    </KhachHangContext.Provider>
  );
}
